import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccounts } from '#hooks/useAccounts';
import { useLocalPref } from '#hooks/useLocalPref';

import { readThisWeekByBank, useBankReads } from './bankReads';

/** The steps of a full recalculation, as the header counts them. */
export const RECALCUL_STEPS = 4;

export type Recalcul = {
  /** The step under way, or null when nothing runs. */
  step: 1 | 2 | 3 | null;
  /** Raised to make every walk recompute, whatever its inputs. */
  generation: number;
  /** When the walk was last computed on this device. */
  doneAt: number | null;
  /** When the server and the banks were last read. */
  dataAt: number | null;
  /** How the last recalculation asked by hand went. */
  outcome: 'done' | 'offline' | null;
  /** The self-checks of the last walk: how many hold, out of how many. */
  checks: { ok: number; total: number } | null;
};

// Kept for the whole session and shared by every screen: a recalculation
// started on one screen goes on, and is read, on the next.
let state: Recalcul = {
  step: null,
  generation: 0,
  doneAt: null,
  dataAt: null,
  outcome: null,
  checks: null,
};
const listeners = new Set<() => void>();

export function getRecalcul(): Recalcul {
  return state;
}

export function setRecalcul(next: Partial<Recalcul>) {
  state = { ...state, ...next };
  listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useRecalcul(): Recalcul {
  return useSyncExternalStore(subscribe, getRecalcul);
}

export type UnreadAccount = {
  id: string;
  /** The account at the bank bridge, the key of the banks' reads. */
  accountId: string | null;
  name: string;
  /** Why the bank could not be read, in words. */
  reason: string;
  lastSync: string | null;
};

/**
 * The accounts whose bank could not be read, whatever the others did: one
 * unreadable account must never pass for « the server does not answer ».
 * One read a week, on Sunday, is enough to plan it: an account whose bank
 * sent data since Sunday is left out, however its connection fares since.
 * The day is the bank's own: a read that brings nothing new does not count.
 */
export function useUnreadAccounts(): UnreadAccount[] {
  const { t } = useTranslation();
  const { data: accounts = [] } = useAccounts();
  // Read again after every bank sync: the screen follows the new days.
  useBankReads();
  return accounts
    .filter(
      account =>
        !account.closed &&
        account.account_sync_source &&
        account.bank_sync_status &&
        account.bank_sync_status !== 'ok' &&
        !readThisWeekByBank(account),
    )
    .map(account => ({
      id: account.id,
      accountId: account.account_id ?? null,
      name: account.name,
      reason:
        account.bank_sync_status === 'account-missing'
          ? t('introuvable à la banque')
          : account.bank_sync_status === 'timed-out'
            ? t('délai dépassé')
            : account.bank_sync_status === 'reauth-required' ||
                account.bank_sync_status === 'attention-required'
              ? t('connexion à la banque à refaire')
              : t('à vérifier'),
      lastSync: account.last_sync ?? null,
    }));
}

/**
 * Whether the message of this kind was closed by hand while it said `key`, on
 * this device. A message that says something new opens again.
 */
export function useDismissed(
  kind: string,
  key: string,
): [dismissed: boolean, dismiss: () => void] {
  const [dismissed, setDismissed] = useLocalPref('mtxbudget.dismissed');
  return [
    dismissed?.[kind] === key,
    () => setDismissed({ ...dismissed, [kind]: key }),
  ];
}
